import React, { useState } from "react";
import { sortBy, xor } from "lodash";
import { ChevronDown } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { CheckboxDropdownItem } from "@sonder/ui/source/styled/fragments/CheckboxDropdownItem";
import {
  FilterButtonList,
  FilterDropdownContent,
  FilterEmpty,
  FilterFooter,
  FilterTextArea,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { useStore as useAuthStore } from "auth/store";
import { useRolesBasedGetCleaningCompanies } from "core/queries/GetCleaningCompanies";
import { setFilters } from "schedule/actions";
import { areCleaningCompanyIdsActive } from "schedule/helpers";
import {
  FilterPair,
  ScheduleFilterInput,
  initialState,
} from "schedule/reducer";
import { useStore } from "schedule/store";
import { useSegmentQueue } from "tasks/hooks";

interface FilterGroupItemProps {
  filters: ScheduleFilterInput;
  isActive?(props: FilterPair): boolean;
  label: string;
  segmentId: string;
}

export const CleaningCompanyFiltersGroupItem = (
  props: FilterGroupItemProps,
) => {
  const { filters, isActive = () => false, label, segmentId } = props;
  const {
    state: { filters: globalFilters },
    dispatch,
  } = useStore();
  const pushToSegmentQueue = useSegmentQueue(globalFilters);
  const active = isActive({ globalFilters, localFilters: filters });
  return (
    <CheckboxDropdownItem
      checked={active}
      onClick={() => {
        const newCleaningCompanyIds = xor(
          globalFilters.cleaningCompanyIds,
          filters.cleaningCompanyIds,
        );
        dispatch(setFilters({ cleaningCompanyIds: newCleaningCompanyIds }));

        pushToSegmentQueue({
          action: "click",
          commonProps: {},
          element: segmentId,
          eventName: "schedule_cleaning_company_filters",
          props: { nextValue: !active ? "selected" : "deselected" },
        });
      }}
    >
      {label}
    </CheckboxDropdownItem>
  );
};

export const CleaningCompanyFilters = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const {
    state: { user: loggedInUser },
  } = useAuthStore();
  const {
    state: { filters: globalFilters },
    dispatch,
  } = useStore();
  const cityId = loggedInUser?.cityId;

  const { data, error, loading } = useRolesBasedGetCleaningCompanies({
    skip: !cityId,
    variables: {
      filters: {
        active: true,
        cityId: cityId || "",
      },
    },
  });

  if (!loggedInUser?.isCleaningManager || loading || error) {
    return null;
  }

  const { cleaningCompanyIds } = globalFilters;
  const totalSelected = cleaningCompanyIds?.length || 0;
  const allCompanies = sortBy(data?.cleaningCompany || [], "name");
  const filteredCompanies = searchTerm
    ? allCompanies.filter(cleaningCompany =>
        cleaningCompany.name
          .toLowerCase()
          .includes(searchTerm.toLocaleLowerCase()),
      )
    : allCompanies;

  return (
    <Dropdown
      trigger={
        <Button fullWidth iconRight type="button">
          Companies
          {totalSelected > 0 && ` (${totalSelected})`}
          <ChevronDown />
        </Button>
      }
    >
      {({ isOpen: isPortalOpen }) =>
        isPortalOpen && (
          <FilterDropdownContent align="center">
            <FilterTextArea
              autoFocus
              placeholder={`Search ${allCompanies.length} companies`}
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
              }}
            />
            {searchTerm && filteredCompanies.length === 0 && (
              <FilterEmpty>{`No Results for ${searchTerm}.`}</FilterEmpty>
            )}
            <FilterButtonList>
              {filteredCompanies.length > 0 &&
                filteredCompanies.map(cleaningCompany => (
                  <CleaningCompanyFiltersGroupItem
                    filters={{ cleaningCompanyIds: [cleaningCompany.id] }}
                    isActive={areCleaningCompanyIdsActive}
                    key={cleaningCompany.id}
                    label={cleaningCompany.name}
                    segmentId={`cleaning_company_id_${cleaningCompany.id}`}
                  />
                ))}
            </FilterButtonList>
            <FilterFooter>
              {`${totalSelected || 0} Selected`}
              {totalSelected > 0 && (
                <Link
                  onClick={() => {
                    requestAnimationFrame(() =>
                      dispatch(
                        setFilters({
                          cleaningCompanyIds:
                            initialState.filters.cleaningCompanyIds,
                        }),
                      ),
                    );
                  }}
                >
                  Clear selected
                </Link>
              )}
            </FilterFooter>
          </FilterDropdownContent>
        )
      }
    </Dropdown>
  );
};
